import React, { useEffect, useMemo, useState } from "react";
import {
    adminListInvoices,
    adminCreateInvoice,
    adminUpdateInvoice,
    adminDeleteInvoice,
    adminInvoiceAction,
    adminGetInvoiceSettings,
} from "@/lib/api";

/**
 * Invoice console.
 *
 * The rules the API enforces are mirrored in what this page offers, so the
 * interface never presents a button that will be refused: Edit and Delete
 * appear only on drafts, Void only on something that has a number worth
 * preserving. The server is still the authority — this is a courtesy, not the
 * control.
 */

const CURRENCIES = ["INR", "USD", "EUR", "GBP", "AED", "SGD", "AUD", "CAD"];
const PAID_METHODS = ["UPI", "NEFT", "IMPS", "RTGS", "Bank transfer", "Other"];

const CURRENCY_LOCALE = {
    INR: "en-IN",
    USD: "en-US",
    EUR: "en-IE",
    GBP: "en-GB",
    AED: "en-AE",
    SGD: "en-SG",
    AUD: "en-AU",
    CAD: "en-CA",
};

function money(minor, currency) {
    const value = (Number(minor) || 0) / 100;
    try {
        return new Intl.NumberFormat(CURRENCY_LOCALE[currency] || "en-IN", {
            style: "currency",
            currency: currency || "INR",
            minimumFractionDigits: 2,
        }).format(value);
    } catch {
        return `${currency} ${value.toFixed(2)}`;
    }
}

/** Minor units back into something typeable: 4500000 → "45000.00" */
function toInput(minor) {
    const n = Number(minor) || 0;
    return (n / 100).toFixed(2);
}

function today() {
    return new Date().toISOString().slice(0, 10);
}

function plusDays(days) {
    const d = new Date();
    d.setDate(d.getDate() + days);
    return d.toISOString().slice(0, 10);
}

const emptyLine = () => ({ description: "", qty: "1", rate: "" });

const emptyInvoice = () => ({
    new: true,
    issuer_key: "",
    client_name: "",
    client_contact: "",
    client_email: "",
    client_address: "",
    client_country: "IN",
    client_tax_id: "",
    title: "",
    item_mode: "single",
    single_description: "",
    single_amount: "",
    lines: [emptyLine()],
    currency: "INR",
    discount: "0.00",
    tax_mode: "none",
    tax_rate_bps: 1800,
    place_of_supply: "",
    issue_date: today(),
    due_date: plusDays(15),
    terms: "",
    notes: "",
});

/** Server shape → form shape. */
function toForm(row) {
    const items = Array.isArray(row.items) ? row.items : [];
    const first = items[0] || {};
    return {
        new: false,
        id: row.id,
        status: row.status,
        issuer_key: row.issuer_key || "",
        client_name: row.client_name,
        client_contact: row.client_contact,
        client_email: row.client_email,
        client_address: row.client_address,
        client_country: row.client_country || "IN",
        client_tax_id: row.client_tax_id,
        title: row.title,
        item_mode: row.item_mode,
        single_description: first.description || "",
        single_amount: toInput(first.amount_minor),
        lines: items.length
            ? items.map((i) => ({
                  description: i.description || "",
                  qty: String(i.qty ?? 1),
                  rate: toInput(i.rate_minor),
              }))
            : [emptyLine()],
        currency: row.currency || "INR",
        discount: toInput(row.discount_minor),
        tax_mode: row.tax_mode || "none",
        tax_rate_bps: row.tax_rate_bps || 1800,
        place_of_supply: row.place_of_supply || "",
        issue_date: row.issue_date || today(),
        due_date: row.due_date || "",
        terms: row.terms || "",
        notes: row.notes || "",
    };
}

/** Form shape → API payload. Amounts go as typed; the server parses and totals. */
function toPayload(form) {
    const items =
        form.item_mode === "lines"
            ? form.lines.map((l) => ({
                  description: l.description,
                  qty: l.qty,
                  rate: l.rate,
              }))
            : [{ description: form.single_description, amount: form.single_amount }];

    return {
        issuer_key: form.issuer_key,
        client_name: form.client_name,
        client_contact: form.client_contact,
        client_email: form.client_email,
        client_address: form.client_address,
        client_country: form.client_country,
        client_tax_id: form.client_tax_id,
        title: form.title,
        item_mode: form.item_mode,
        items,
        currency: form.currency,
        discount: form.discount,
        tax_mode: form.tax_mode,
        tax_rate_bps: Number(form.tax_rate_bps) || 0,
        place_of_supply: form.place_of_supply,
        issue_date: form.issue_date,
        due_date: form.due_date,
        terms: form.terms,
        notes: form.notes,
    };
}

/**
 * Local echo of the server's arithmetic, for the running total in the form.
 *
 * Anything unparseable is reported rather than counted as zero. A stray third
 * decimal place is an easy thing to type and, treated as nothing, would leave a
 * line silently worth ₹0.00 inside a total that still looked reasonable. The
 * server refuses these too; this is so it is visible before the save.
 */
function preview(form) {
    const invalid = [];
    const parse = (v, label) => {
        const s = String(v ?? "").trim().replace(/[\s,]/g, "");
        if (!s) return 0;
        if (!/^\d*(\.\d{0,2})?$/.test(s)) {
            invalid.push(label);
            return 0;
        }
        const [whole = "0", frac = ""] = s.split(".");
        return Number(whole || 0) * 100 + Number((frac + "00").slice(0, 2));
    };
    const qty = (v, label) => {
        const s = String(v ?? "").trim();
        const n = Number(s);
        if (s === "" || !Number.isFinite(n) || n < 0) {
            invalid.push(label);
            return 0;
        }
        return n;
    };

    const subtotal =
        form.item_mode === "lines"
            ? form.lines.reduce(
                  (sum, l, i) =>
                      sum +
                      Math.round(
                          qty(l.qty, `line ${i + 1} quantity`) *
                              parse(l.rate, `line ${i + 1} rate`),
                      ),
                  0,
              )
            : parse(form.single_amount, "the amount");

    const discount = Math.max(0, Math.min(subtotal, parse(form.discount, "the discount")));
    const rate = form.tax_mode === "none" ? 0 : Number(form.tax_rate_bps) || 0;
    const tax = Math.round(((subtotal - discount) * rate) / 10000);
    return { subtotal, discount, tax, total: subtotal - discount + tax, invalid };
}

export default function InvoicesPage() {
    const [items, setItems] = useState([]);
    const [settings, setSettings] = useState(null);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [payFor, setPayFor] = useState(null);
    const [copied, setCopied] = useState("");

    const formatError = (e) => {
        const d = e?.response?.data?.detail;
        if (typeof d === "string") return d;
        if (d && typeof d === "object") return JSON.stringify(d);
        return e?.message || "Unknown error";
    };

    const load = async () => {
        setLoading(true);
        try {
            const [rows, config] = await Promise.all([
                adminListInvoices(),
                adminGetInvoiceSettings().catch(() => null),
            ]);
            setItems(rows);
            setSettings(config);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const outstanding = useMemo(() => {
        const byCurrency = {};
        for (const row of items) {
            if (row.status !== "issued") continue;
            byCurrency[row.currency] = (byCurrency[row.currency] || 0) + row.total_minor;
        }
        return Object.entries(byCurrency);
    }, [items]);

    const profiles = settings?.profiles || [];
    const profileByKey = (key) =>
        profiles.find((p) => p.key === key) ||
        profiles.find((p) => p.key === settings?.default_key) ||
        profiles[0] ||
        null;

    // Named individually: Depmont being ready says nothing about a second
    // identity having its own account filled in, and issuing is refused per
    // profile rather than globally.
    const unusable = profiles.filter(
        (p) => !p.upi_id && !p.bank_account_number,
    );

    const openNew = () => {
        setError("");
        const base = emptyInvoice();
        const profile = profileByKey(settings?.default_key);
        setEditing({
            ...base,
            issuer_key: profile?.key || "",
            terms: profile?.default_terms || "",
            notes: profile?.default_notes || "",
        });
    };

    const openEdit = (row) => {
        setError("");
        setEditing(toForm(row));
    };

    const setField = (k, v) => setEditing((e) => ({ ...e, [k]: v }));

    /**
     * Switching identity also swaps the default wording — but only where it has
     * not been edited. Overwriting terms someone had typed by hand because they
     * changed the issuer afterwards would be the more annoying of the two
     * failures.
     */
    const setIssuer = (key) =>
        setEditing((e) => {
            const from = profileByKey(e.issuer_key);
            const to = profileByKey(key);
            return {
                ...e,
                issuer_key: key,
                terms: e.terms === (from?.default_terms || "") ? to?.default_terms || "" : e.terms,
                notes: e.notes === (from?.default_notes || "") ? to?.default_notes || "" : e.notes,
            };
        });

    const setLine = (i, k, v) =>
        setEditing((e) => ({
            ...e,
            lines: e.lines.map((l, idx) => (idx === i ? { ...l, [k]: v } : l)),
        }));

    const save = async () => {
        setError("");
        if (!editing.client_name.trim()) {
            setError("A client name is required.");
            return;
        }
        const { invalid } = preview(editing);
        if (invalid.length) {
            setError(
                `Check ${invalid.join(", ")} — use digits and at most two decimal places.`,
            );
            return;
        }
        setSaving(true);
        try {
            const payload = toPayload(editing);
            if (editing.new) {
                await adminCreateInvoice(payload);
            } else {
                await adminUpdateInvoice(editing.id, payload);
            }
            setEditing(null);
            await load();
        } catch (e) {
            setError("Save failed — " + formatError(e));
        } finally {
            setSaving(false);
        }
    };

    const act = async (row, payload, confirmText) => {
        if (confirmText && !window.confirm(confirmText)) return;
        try {
            await adminInvoiceAction(row.id, payload);
            await load();
        } catch (e) {
            window.alert(formatError(e));
        }
    };

    const issue = (row) =>
        act(
            row,
            { action: "issue" },
            `Issue this invoice to ${row.client_name}?\n\nIt will be given a permanent number and can no longer be edited. To correct it afterwards you would void it and issue a replacement.`,
        );

    const voidIt = async (row) => {
        const reason = window.prompt(
            `Void ${row.number}?\n\nThe number stays on record — that is the point of voiding rather than deleting. Give a short reason:`,
        );
        if (reason === null) return;
        if (!reason.trim()) {
            window.alert("A reason is required.");
            return;
        }
        await act(row, { action: "void", void_reason: reason });
    };

    const del = async (row) => {
        if (
            !window.confirm(
                "Delete this draft? It has no invoice number yet, so nothing is left behind.",
            )
        )
            return;
        try {
            await adminDeleteInvoice(row.id);
            await load();
        } catch (e) {
            window.alert(formatError(e));
        }
    };

    const copyLink = async (row) => {
        const url = `${window.location.origin}${row.url}`;
        try {
            await navigator.clipboard.writeText(url);
            setCopied(row.id);
            setTimeout(() => setCopied(""), 2000);
        } catch {
            window.prompt("Copy this link:", url);
        }
    };

    return (
        <section data-testid="admin-invoices">
            <header className="flex items-end justify-between border-b border-[#E6E2D6] pb-6 mb-8">
                <div>
                    <p className="eyebrow text-[#1B1B1B]/60 mb-2">§ Invoices — 03</p>
                    <h1 className="font-serif-editorial text-4xl md:text-5xl leading-[1.02] tracking-[-0.02em]">
                        Invoices
                    </h1>
                </div>
                <button
                    type="button"
                    onClick={openNew}
                    data-testid="invoice-new"
                    className="inline-flex items-center gap-3 px-7 py-3 bg-[#1B1B1B] text-[#F6F3EC] font-sans-tight text-[12px] tracking-[0.2em] uppercase hover:bg-[#A8844F] transition-colors duration-500"
                >
                    + New Invoice
                </button>
            </header>

            {unusable.length > 0 && (
                <div className="border border-[#A8844F]/40 bg-[#A8844F]/5 p-5 mb-8">
                    <p className="eyebrow text-[#A8844F] mb-2">Before you can issue</p>
                    <p className="font-sans-tight text-sm text-[#1B1B1B]/75 font-light leading-[1.7]">
                        {unusable.map((p) => p.label).join(" and ")}{" "}
                        {unusable.length > 1 ? "have" : "has"} no UPI ID or bank account
                        yet. Add one under{" "}
                        <span className="text-[#1B1B1B]">Invoice Settings</span> in the
                        sidebar — without it a client who opens the invoice has no way to
                        pay, so issuing under{" "}
                        {unusable.length > 1 ? "those identities" : "that identity"} is
                        blocked.
                    </p>
                </div>
            )}

            <div className="font-sans-tight text-sm text-[#1B1B1B]/70 mb-8 max-w-2xl font-light leading-[1.7]">
                <p>
                    A draft can be changed as often as you like. Issuing gives it a
                    permanent number and freezes it, and only then does its link work.
                </p>
                {outstanding.length > 0 && (
                    <p className="mt-3">
                        Outstanding ·{" "}
                        {outstanding.map(([cur, sum], i) => (
                            <span key={cur} className="text-[#1B1B1B]">
                                {i > 0 ? " · " : ""}
                                {money(sum, cur)}
                            </span>
                        ))}
                    </p>
                )}
            </div>

            {loading ? (
                <p className="eyebrow text-[#1B1B1B]/50">Loading…</p>
            ) : items.length === 0 ? (
                <p className="font-serif-editorial italic text-2xl text-[#1B1B1B]/70">
                    No invoices yet.
                </p>
            ) : (
                <div className="border-t border-[#E6E2D6]">
                    {items.map((row) => (
                        <div
                            key={row.id}
                            data-testid={`invoice-row-${row.id}`}
                            className="grid grid-cols-12 gap-4 items-center py-5 border-b border-[#E6E2D6]"
                        >
                            <div className="col-span-12 md:col-span-5">
                                <p className="eyebrow text-[#1B1B1B]/60 mb-1">
                                    {row.number || "Draft"} ·{" "}
                                    {row.issue_date || "no date"}
                                    {profiles.length > 1 && (
                                        <span className="text-[#A8844F]">
                                            {" "}
                                            ·{" "}
                                            {profileByKey(row.issuer_key)?.label ||
                                                row.issuer_key}
                                        </span>
                                    )}
                                </p>
                                <h3 className="font-serif-editorial text-2xl leading-[1.1]">
                                    {row.client_name || "Unnamed client"}
                                </h3>
                                {row.title && (
                                    <p className="font-sans-tight text-xs text-[#1B1B1B]/60 mt-1 font-light">
                                        {row.title}
                                    </p>
                                )}
                            </div>

                            <div className="col-span-6 md:col-span-3">
                                <p
                                    className="font-sans-tight text-sm"
                                    style={{ fontVariantNumeric: "tabular-nums" }}
                                >
                                    {money(row.total_minor, row.currency)}
                                </p>
                                <StatusTag row={row} />
                            </div>

                            <div className="col-span-6 md:col-span-4 flex flex-wrap gap-x-5 gap-y-2 justify-end">
                                {row.status === "draft" && (
                                    <>
                                        <button
                                            type="button"
                                            onClick={() => openEdit(row)}
                                            data-testid={`invoice-edit-${row.id}`}
                                            className="eyebrow link-underline"
                                        >
                                            Edit
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => issue(row)}
                                            data-testid={`invoice-issue-${row.id}`}
                                            className="eyebrow link-underline text-[#A8844F]"
                                        >
                                            Issue
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => del(row)}
                                            className="eyebrow link-underline text-red-800"
                                        >
                                            Delete
                                        </button>
                                    </>
                                )}

                                {row.status !== "draft" && (
                                    <>
                                        <a
                                            href={row.url}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="eyebrow link-underline"
                                        >
                                            View ↗
                                        </a>
                                        <button
                                            type="button"
                                            onClick={() => copyLink(row)}
                                            data-testid={`invoice-copy-${row.id}`}
                                            className="eyebrow link-underline"
                                        >
                                            {copied === row.id ? "Copied ✓" : "Copy link"}
                                        </button>
                                    </>
                                )}

                                {row.status === "issued" && (
                                    <>
                                        <button
                                            type="button"
                                            onClick={() => setPayFor(row)}
                                            data-testid={`invoice-paid-${row.id}`}
                                            className="eyebrow link-underline text-[#A8844F]"
                                        >
                                            Mark paid
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => voidIt(row)}
                                            className="eyebrow link-underline text-red-800"
                                        >
                                            Void
                                        </button>
                                    </>
                                )}

                                {row.status === "paid" && (
                                    <button
                                        type="button"
                                        onClick={() =>
                                            act(
                                                row,
                                                { action: "unpaid" },
                                                "Mark this invoice unpaid again? Use this only if the payment was recorded in error.",
                                            )
                                        }
                                        className="eyebrow link-underline"
                                    >
                                        Undo paid
                                    </button>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {editing && (
                <Editor
                    profiles={profiles}
                    editing={editing}
                    setField={setField}
                    setIssuer={setIssuer}
                    setLine={setLine}
                    setEditing={setEditing}
                    save={save}
                    saving={saving}
                    error={error}
                    close={() => setEditing(null)}
                />
            )}

            {payFor && (
                <PaidDialog
                    row={payFor}
                    close={() => setPayFor(null)}
                    confirm={async (payload) => {
                        setPayFor(null);
                        await act(payFor, { action: "paid", ...payload });
                    }}
                />
            )}
        </section>
    );
}

const StatusTag = ({ row }) => {
    const overdue =
        row.status === "issued" && row.due_date && row.due_date < today();
    const label = overdue ? "Overdue" : row.status;
    const tone =
        row.status === "paid"
            ? "text-[#A8844F]"
            : overdue || row.status === "void"
              ? "text-red-800"
              : "text-[#1B1B1B]/60";
    return (
        <p className={`eyebrow mt-1 ${tone}`}>
            {label}
            {row.status === "issued" && row.due_date
                ? ` · due ${row.due_date}`
                : ""}
        </p>
    );
};

function Editor({
    profiles,
    editing,
    setField,
    setIssuer,
    setLine,
    setEditing,
    save,
    saving,
    error,
    close,
}) {
    const totals = preview(editing);
    const lines = editing.item_mode === "lines";
    const issuer = profiles.find((p) => p.key === editing.issuer_key) || profiles[0];

    const addLine = () =>
        setEditing((e) => ({ ...e, lines: [...e.lines, emptyLine()] }));
    const removeLine = (i) =>
        setEditing((e) => ({
            ...e,
            lines: e.lines.length > 1 ? e.lines.filter((_, idx) => idx !== i) : e.lines,
        }));

    return (
        <div className="fixed inset-0 z-50 bg-[#1B1B1B]/40 flex justify-end">
            <div className="w-full max-w-3xl h-full overflow-y-auto bg-[#F6F3EC] p-8 md:p-12">
                <div className="flex items-center justify-between mb-8">
                    <p className="eyebrow text-[#1B1B1B]/60">
                        {editing.new ? "New Invoice" : "Edit Draft"}
                    </p>
                    <button type="button" onClick={close} className="eyebrow link-underline">
                        Close ✕
                    </button>
                </div>

                <div className="space-y-8">
                    {profiles.length > 1 && (
                        <div>
                            <p className="eyebrow text-[#A8844F] mb-4">Issued by</p>
                            <Field
                                label="Identity"
                                hint="Which of your identities raises this invoice. It sets the name, address and bank details on the document, and which run of invoice numbers it takes from. Changeable while this is a draft; fixed once issued, because the number already carries the prefix."
                            >
                                <select
                                    className="editorial-input"
                                    value={editing.issuer_key}
                                    onChange={(e) => setIssuer(e.target.value)}
                                    data-testid="invoice-issuer"
                                >
                                    {profiles.map((p) => (
                                        <option key={p.key} value={p.key}>
                                            {p.label}
                                            {p.number_prefix ? ` — ${p.number_prefix}-…` : ""}
                                        </option>
                                    ))}
                                </select>
                            </Field>
                            {issuer && (
                                <p className="mt-3 font-sans-tight text-xs text-[#1B1B1B]/55 font-light leading-[1.6]">
                                    Will be billed as{" "}
                                    <span className="text-[#1B1B1B]">
                                        {issuer.trading_name || issuer.legal_name || issuer.label}
                                    </span>
                                    {issuer.upi_id || issuer.bank_account_number
                                        ? ", paid to " +
                                          (issuer.upi_id ||
                                              issuer.bank_account_name ||
                                              "the account on file")
                                        : " — no payment details set, so this cannot be issued yet"}
                                    .
                                </p>
                            )}
                        </div>
                    )}

                    <div>
                        <p className="eyebrow text-[#A8844F] mb-4">Client</p>
                        <div className="space-y-6">
                            <Field label="Client name" hint="The organisation or person being billed.">
                                <input
                                    className="editorial-input"
                                    value={editing.client_name}
                                    onChange={(e) => setField("client_name", e.target.value)}
                                    placeholder="Exophian AI"
                                    data-testid="invoice-client-name"
                                />
                            </Field>
                            <div className="grid grid-cols-2 gap-6">
                                <Field label="Contact person">
                                    <input
                                        className="editorial-input"
                                        value={editing.client_contact}
                                        onChange={(e) => setField("client_contact", e.target.value)}
                                    />
                                </Field>
                                <Field label="Email">
                                    <input
                                        className="editorial-input"
                                        value={editing.client_email}
                                        onChange={(e) => setField("client_email", e.target.value)}
                                    />
                                </Field>
                            </div>
                            <Field label="Address" hint="Line breaks are kept as typed.">
                                <textarea
                                    rows={3}
                                    className="editorial-input resize-none"
                                    value={editing.client_address}
                                    onChange={(e) => setField("client_address", e.target.value)}
                                />
                            </Field>
                            <div className="grid grid-cols-2 gap-6">
                                <Field label="Country code" hint="Two letters. IN for India.">
                                    <input
                                        className="editorial-input"
                                        value={editing.client_country}
                                        maxLength={2}
                                        onChange={(e) =>
                                            setField("client_country", e.target.value.toUpperCase())
                                        }
                                    />
                                </Field>
                                <Field label="Client GSTIN / VAT" hint="Optional. Leave blank if they have none.">
                                    <input
                                        className="editorial-input"
                                        value={editing.client_tax_id}
                                        onChange={(e) => setField("client_tax_id", e.target.value)}
                                    />
                                </Field>
                            </div>
                        </div>
                    </div>

                    <div>
                        <p className="eyebrow text-[#A8844F] mb-4">Work</p>
                        <div className="space-y-6">
                            <Field label="Project title" hint="Appears under 'Project' on the invoice.">
                                <input
                                    className="editorial-input"
                                    value={editing.title}
                                    onChange={(e) => setField("title", e.target.value)}
                                    placeholder="Brand Identity System"
                                />
                            </Field>

                            <Field
                                label="Billing style"
                                hint="A single fee is one description and one amount. Line items adds quantity and rate, and totals itself."
                            >
                                <select
                                    className="editorial-input"
                                    value={editing.item_mode}
                                    onChange={(e) => setField("item_mode", e.target.value)}
                                    data-testid="invoice-mode"
                                >
                                    <option value="single">Single fee</option>
                                    <option value="lines">Line items</option>
                                </select>
                            </Field>

                            {!lines ? (
                                <div className="grid grid-cols-3 gap-6">
                                    <div className="col-span-2">
                                        <Field label="Description">
                                            <input
                                                className="editorial-input"
                                                value={editing.single_description}
                                                onChange={(e) =>
                                                    setField("single_description", e.target.value)
                                                }
                                                placeholder="Brand identity system"
                                                data-testid="invoice-single-desc"
                                            />
                                        </Field>
                                    </div>
                                    <Field label={`Amount (${editing.currency})`}>
                                        <input
                                            className="editorial-input"
                                            value={editing.single_amount}
                                            onChange={(e) =>
                                                setField("single_amount", e.target.value)
                                            }
                                            placeholder="45000"
                                            inputMode="decimal"
                                            data-testid="invoice-single-amount"
                                        />
                                    </Field>
                                </div>
                            ) : (
                                <div>
                                    <label className="eyebrow text-[#1B1B1B]/60">Line items</label>
                                    <div className="mt-3 space-y-3">
                                        {editing.lines.map((line, i) => (
                                            <div key={i} className="grid grid-cols-12 gap-3 items-end">
                                                <div className="col-span-6">
                                                    <input
                                                        className="editorial-input"
                                                        value={line.description}
                                                        onChange={(e) =>
                                                            setLine(i, "description", e.target.value)
                                                        }
                                                        placeholder="Description"
                                                    />
                                                </div>
                                                <div className="col-span-2">
                                                    <input
                                                        className="editorial-input"
                                                        value={line.qty}
                                                        onChange={(e) => setLine(i, "qty", e.target.value)}
                                                        placeholder="Qty"
                                                        inputMode="decimal"
                                                    />
                                                </div>
                                                <div className="col-span-3">
                                                    <input
                                                        className="editorial-input"
                                                        value={line.rate}
                                                        onChange={(e) => setLine(i, "rate", e.target.value)}
                                                        placeholder="Rate"
                                                        inputMode="decimal"
                                                    />
                                                </div>
                                                <div className="col-span-1 pb-3 text-right">
                                                    <button
                                                        type="button"
                                                        onClick={() => removeLine(i)}
                                                        className="eyebrow text-red-800"
                                                        title="Remove line"
                                                    >
                                                        ✕
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                    <button
                                        type="button"
                                        onClick={addLine}
                                        className="mt-4 eyebrow link-underline text-[#A8844F]"
                                    >
                                        + Add line
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>

                    <div>
                        <p className="eyebrow text-[#A8844F] mb-4">Money</p>
                        <div className="space-y-6">
                            <div className="grid grid-cols-2 gap-6">
                                <Field label="Currency">
                                    <select
                                        className="editorial-input"
                                        value={editing.currency}
                                        onChange={(e) => setField("currency", e.target.value)}
                                    >
                                        {CURRENCIES.map((c) => (
                                            <option key={c} value={c}>
                                                {c}
                                            </option>
                                        ))}
                                    </select>
                                </Field>
                                <Field label="Discount" hint="Leave at 0.00 if there is none.">
                                    <input
                                        className="editorial-input"
                                        value={editing.discount}
                                        onChange={(e) => setField("discount", e.target.value)}
                                        inputMode="decimal"
                                    />
                                </Field>
                            </div>

                            <div className="grid grid-cols-2 gap-6">
                                <Field
                                    label="Tax"
                                    hint="Leave as 'None' until Depmont is registered for GST. An unregistered business may not charge it."
                                >
                                    <select
                                        className="editorial-input"
                                        value={editing.tax_mode}
                                        onChange={(e) => setField("tax_mode", e.target.value)}
                                    >
                                        <option value="none">None — not registered</option>
                                        <option value="gst_intra">GST — same state (CGST + SGST)</option>
                                        <option value="gst_inter">GST — other state (IGST)</option>
                                        <option value="export">Export — zero rated</option>
                                    </select>
                                </Field>
                                {editing.tax_mode !== "none" && (
                                    <Field label="Rate %" hint="18 for most design services.">
                                        <input
                                            className="editorial-input"
                                            value={(Number(editing.tax_rate_bps) || 0) / 100}
                                            onChange={(e) =>
                                                setField(
                                                    "tax_rate_bps",
                                                    Math.round((Number(e.target.value) || 0) * 100),
                                                )
                                            }
                                            inputMode="decimal"
                                        />
                                    </Field>
                                )}
                            </div>

                            {editing.tax_mode !== "none" && (
                                <Field label="Place of supply">
                                    <input
                                        className="editorial-input"
                                        value={editing.place_of_supply}
                                        onChange={(e) => setField("place_of_supply", e.target.value)}
                                        placeholder="Uttar Pradesh"
                                    />
                                </Field>
                            )}

                            <div className="border border-[#E6E2D6] p-5">
                                <TotalRow
                                    label="Subtotal"
                                    value={money(totals.subtotal, editing.currency)}
                                />
                                {totals.discount > 0 && (
                                    <TotalRow
                                        label="Discount"
                                        value={`− ${money(totals.discount, editing.currency)}`}
                                    />
                                )}
                                {totals.tax > 0 && (
                                    <TotalRow
                                        label="Tax"
                                        value={money(totals.tax, editing.currency)}
                                    />
                                )}
                                <div className="hairline my-3" />
                                <div className="flex items-baseline justify-between">
                                    <p className="eyebrow text-[#1B1B1B]/60">Total</p>
                                    <p
                                        className="font-serif-editorial text-2xl"
                                        style={{ fontVariantNumeric: "tabular-nums" }}
                                        data-testid="invoice-preview-total"
                                    >
                                        {money(totals.total, editing.currency)}
                                    </p>
                                </div>
                                {totals.invalid.length > 0 && (
                                    <p
                                        data-testid="invoice-amount-warning"
                                        className="mt-3 font-sans-tight text-xs text-red-800 font-light leading-[1.6]"
                                    >
                                        This total ignores {totals.invalid.join(", ")} —
                                        the value there cannot be read as money. Use digits
                                        and at most two decimal places.
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>

                    <div>
                        <p className="eyebrow text-[#A8844F] mb-4">Dates and wording</p>
                        <div className="space-y-6">
                            <div className="grid grid-cols-2 gap-6">
                                <Field
                                    label="Issue date"
                                    hint="Decides the financial year, so the invoice number follows from it."
                                >
                                    <input
                                        type="date"
                                        className="editorial-input"
                                        value={editing.issue_date}
                                        onChange={(e) => setField("issue_date", e.target.value)}
                                        data-testid="invoice-issue-date"
                                    />
                                </Field>
                                <Field label="Due date">
                                    <input
                                        type="date"
                                        className="editorial-input"
                                        value={editing.due_date}
                                        onChange={(e) => setField("due_date", e.target.value)}
                                    />
                                </Field>
                            </div>
                            <Field label="Payment terms">
                                <textarea
                                    rows={2}
                                    className="editorial-input resize-none"
                                    value={editing.terms}
                                    onChange={(e) => setField("terms", e.target.value)}
                                />
                            </Field>
                            <Field label="Notes" hint="Optional. Anything the client should read alongside the amount.">
                                <textarea
                                    rows={2}
                                    className="editorial-input resize-none"
                                    value={editing.notes}
                                    onChange={(e) => setField("notes", e.target.value)}
                                />
                            </Field>
                        </div>
                    </div>
                </div>

                <div className="mt-10 pt-6 border-t border-[#E6E2D6] flex flex-col gap-4">
                    {error && (
                        <div
                            data-testid="invoice-save-error"
                            className="border border-red-800/40 bg-red-800/5 text-red-800 font-sans-tight text-sm p-4"
                        >
                            {error}
                        </div>
                    )}
                    <p className="font-sans-tight text-xs text-[#1B1B1B]/55 font-light">
                        Saving keeps this a draft. It gets its number, and its link, when
                        you press Issue on the list.
                    </p>
                    <div className="flex justify-end gap-4">
                        <button type="button" onClick={close} className="eyebrow link-underline">
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={save}
                            disabled={saving}
                            data-testid="invoice-save"
                            className="inline-flex items-center gap-3 px-7 py-3 bg-[#1B1B1B] text-[#F6F3EC] font-sans-tight text-[12px] tracking-[0.2em] uppercase hover:bg-[#A8844F] transition-colors duration-500 disabled:opacity-60"
                        >
                            {saving ? "Saving…" : "Save draft"}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

function PaidDialog({ row, close, confirm }) {
    const [date, setDate] = useState(today());
    const [method, setMethod] = useState(PAID_METHODS[0]);
    const [reference, setReference] = useState("");

    return (
        <div className="fixed inset-0 z-50 bg-[#1B1B1B]/40 flex items-center justify-center p-6">
            <div className="w-full max-w-md bg-[#F6F3EC] p-8">
                <p className="eyebrow text-[#1B1B1B]/60 mb-2">Record payment</p>
                <h2 className="font-serif-editorial text-2xl leading-[1.15] mb-6">
                    {row.number} · {money(row.total_minor, row.currency)}
                </h2>

                <div className="space-y-6">
                    <Field label="Date received">
                        <input
                            type="date"
                            className="editorial-input"
                            value={date}
                            onChange={(e) => setDate(e.target.value)}
                            data-testid="paid-date"
                        />
                    </Field>
                    <Field label="Method">
                        <select
                            className="editorial-input"
                            value={method}
                            onChange={(e) => setMethod(e.target.value)}
                        >
                            {PAID_METHODS.map((m) => (
                                <option key={m} value={m}>
                                    {m}
                                </option>
                            ))}
                        </select>
                    </Field>
                    <Field
                        label="Reference"
                        hint="The UTR or transaction reference from your bank. Kept for your records and shown on the settled invoice."
                    >
                        <input
                            className="editorial-input"
                            value={reference}
                            onChange={(e) => setReference(e.target.value)}
                            data-testid="paid-reference"
                        />
                    </Field>
                </div>

                <div className="mt-8 flex justify-end gap-4">
                    <button type="button" onClick={close} className="eyebrow link-underline">
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={() =>
                            confirm({
                                paid_date: date,
                                paid_method: method,
                                paid_reference: reference,
                            })
                        }
                        data-testid="paid-confirm"
                        className="inline-flex items-center gap-3 px-7 py-3 bg-[#1B1B1B] text-[#F6F3EC] font-sans-tight text-[12px] tracking-[0.2em] uppercase hover:bg-[#A8844F] transition-colors duration-500"
                    >
                        Mark paid
                    </button>
                </div>
            </div>
        </div>
    );
}

const TotalRow = ({ label, value }) => (
    <div className="flex items-baseline justify-between py-1">
        <p className="font-sans-tight text-xs text-[#1B1B1B]/60 font-light">{label}</p>
        <p
            className="font-sans-tight text-sm font-light"
            style={{ fontVariantNumeric: "tabular-nums" }}
        >
            {value}
        </p>
    </div>
);

const Field = ({ label, hint, children }) => (
    <div>
        <label className="eyebrow text-[#1B1B1B]/60">{label}</label>
        {children}
        {hint && (
            <p className="mt-2 font-sans-tight text-xs text-[#1B1B1B]/55 leading-[1.5] max-w-md font-light">
                {hint}
            </p>
        )}
    </div>
);
